import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { GameChoice, GeneratedSet, LottoDb, LottoDbKr, MatchInfo } from '../types/lotto';
import { loadKrDb, loadUsDb } from '../lib/loadDb';
import {
  buildJackpotIndex,
  drawsFor,
  findBestMatch,
  GAMES,
  generateSets,
  isHistoricalJackpot,
  isJackpotMatch,
  isKrGame,
} from '../lib/lotto';
import { AutoSearchHud } from '../components/AutoSearchHud';
import { LottoSetList } from '../components/LottoSetList';
import { MatchInfoPanel } from '../components/MatchInfoPanel';
import { AdSenseSlot } from '../components/AdSenseSlot';
import { JsonLd } from '../components/JsonLd';
import { usePageMeta } from '../hooks/usePageMeta';
import { downloadSetsAsPng } from '../lib/canvas';
import { useAppLocale } from '../hooks/useAppLocale';
import { useGameDisplayName } from '../hooks/useGameDisplayName';
import { useI18nLocaleSync } from '../hooks/useI18nLocaleSync';
import { footerDomainAutoGenerate, footerDomainBase, htmlLangTag, pathWithLocale } from '../lib/locale';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { LottoBallsBounceIcon } from '../components/LottoBallsBounceIcon';

const BATCH_PER_FRAME = 200;

export function Home() {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const location = useLocation();
  const gameDisplayName = useGameDisplayName();

  useI18nLocaleSync();

  usePageMeta({
    title: t('home.metaTitle'),
    description: t('home.metaDesc'),
    path: pathWithLocale('/', locale),
  });

  const [dbUs, setDbUs] = useState<LottoDb | null>(null);
  const [dbKr, setDbKr] = useState<LottoDbKr | null>(null);
  const [game, setGame] = useState<GameChoice>('us_powerball');

  const [autoSets, setAutoSets] = useState<GeneratedSet[]>([]);
  const [autoMatches, setAutoMatches] = useState<(MatchInfo | null)[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [spinAttemptsLive, setSpinAttemptsLive] = useState(0);
  const [spinSummaryAttempts, setSpinSummaryAttempts] = useState<number | null>(null);

  const [autoCopyLabel, setAutoCopyLabel] = useState(() => t('home.copy'));
  const spinCancelRef = useRef(false);

  useEffect(() => {
    loadUsDb().then(setDbUs);
    loadKrDb().then(setDbKr);
  }, []);

  useEffect(() => {
    if (location.hash !== '#auto-search') return;
    const id = window.setTimeout(() => {
      document.getElementById('auto-search')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.hash]);

  const rules = GAMES[game];
  const krMode = isKrGame(game);
  const draws = drawsFor(game, dbUs, dbKr);
  const jackpotIndex = useMemo(() => (draws ? buildJackpotIndex(draws, rules) : null), [draws, rules]);

  const onGameChange = (g: GameChoice) => {
    setGame(g);
    setAutoSets([]);
    setAutoMatches([]);
    setSpinSummaryAttempts(null);
  };

  const bonusName = krMode ? (locale === 'ko' ? rules.bonusName : t('bonus.kr')) : rules.bonusName;

  const spinFindHistorical = () => {
    if (spinning) return;
    if (!draws || !jackpotIndex) {
      window.alert(t('autoGen.alertNoDb'));
      return;
    }

    setSpinning(true);
    spinCancelRef.current = false;
    setSpinSummaryAttempts(null);
    setSpinAttemptsLive(0);

    let attempts = 0;
    const step = () => {
      if (spinCancelRef.current) {
        setSpinning(false);
        return;
      }
      let found: GeneratedSet[] | null = null;
      for (let b = 0; b < BATCH_PER_FRAME && !found; b++) {
        attempts += 1;
        const batch = generateSets(rules);
        if (batch.some((set) => isHistoricalJackpot(jackpotIndex, rules, set))) found = batch;
      }
      setSpinAttemptsLive(attempts);
      if (found) {
        setAutoSets(found);
        setAutoMatches(found.map((set) => findBestMatch(draws, set)));
        setSpinSummaryAttempts(attempts);
        setSpinning(false);
      } else {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  };

  const stopSpin = () => {
    spinCancelRef.current = true;
  };

  useEffect(() => {
    return () => {
      spinCancelRef.current = true;
    };
  }, []);

  const footerUrl = footerDomainBase(locale);
  const autoFooterLine = t('autoGen.generatedAt', { url: footerDomainAutoGenerate(locale) });

  const autoShareText = useMemo(() => {
    if (!autoSets.length) return '';
    const name = gameDisplayName(game);
    const lines = autoSets.map((s) => `${s.regular.join(', ')} + ${s.powerball}`);
    return t('home.shareLine', { game: name, lines: lines.join('\n'), url: footerUrl });
  }, [autoSets, game, t, gameDisplayName, footerUrl]);

  const copyNumbersAuto = () => {
    if (!autoSets.length) return;
    const lines = autoSets.map(
      (s, i) => `${t('home.setLabel', { n: i + 1 })}: ${s.regular.join(', ')} + ${bonusName}: ${s.powerball}`,
    );
    const header = t('autoGen.copyHeader', { game: gameDisplayName(game) });
    const text = `${header}\n${lines.join('\n')}\n\n${autoFooterLine}`;
    navigator.clipboard.writeText(text).then(() => {
      setAutoCopyLabel(t('home.copied'));
      window.setTimeout(() => setAutoCopyLabel(t('home.copy')), 2000);
    });
  };

  const saveAsImageAuto = () => {
    downloadSetsAsPng(autoSets, gameDisplayName(game), 'lotto-numbers.png', footerUrl);
  };

  const termsPath = pathWithLocale('/terms', locale);

  const structuredData = useMemo(
    () => ({
      webApp: {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Free Lotto Number Generator',
        description: t('home.metaDesc'),
        applicationCategory: 'UtilityApplication',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: locale === 'ko' ? 'KRW' : 'USD' },
        browserRequirements: 'Requires JavaScript',
        inLanguage: htmlLangTag(locale),
      },
      howTo: {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('home.schemaHowToName'),
        description: t('home.metaDesc'),
        totalTime: 'PT30S',
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('home.schemaStep1Name'),
            text: t('home.schemaStep1Text'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('home.schemaStepAutoName'),
            text: t('home.schemaStepAutoText'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('home.schemaStep3Name'),
            text: t('home.schemaStep3Text'),
          },
          {
            '@type': 'HowToStep',
            position: 4,
            name: t('home.schemaStep4Name'),
            text: t('home.schemaStep4Text'),
          },
        ],
      },
    }),
    [t, locale],
  );

  const dbOk = !!draws;
  const simPath = pathWithLocale('/simulate', locale);
  const lp = useLocalizedPath();
  const attemptsSummaryKey = locale === 'ko' ? 'autoGen.attemptsDoneKr' : 'autoGen.attemptsDone';

  return (
    <main className="content">
      <JsonLd id="ld-webapp" data={structuredData.webApp} />
      <JsonLd id="ld-howto" data={structuredData.howTo} />
      <nav className="breadcrumb" aria-label={t('layout.breadcrumbLabel')}>
        <span className="current">{t('breadcrumb.home')}</span>
      </nav>

      <section id="auto-search" className="center-block">
        <h1 style={{ fontSize: '1.65em', marginBottom: 12 }}>{t('autoGen.title')}</h1>
        <p className="subtitle">
          <Trans
            i18nKey="autoGen.subtitle"
            components={{
              simLink: <Link to={simPath} />,
              termsLink: <Link to={termsPath} />,
              convenience: <strong />,
            }}
          />
        </p>

        {!dbOk && (
          <div className="db-status-warning" role="alert">
            {krMode ? t('autoGen.dbWarningKr') : t('autoGen.dbWarningUs')}
          </div>
        )}

        {krMode && (
          <div className="feature-banner">
            <p>
              <span dangerouslySetInnerHTML={{ __html: t('home.featureBannerKr') }} />{' '}
              <Link to={simPath}>{t('home.simulateLinkKo')}</Link>
            </p>
          </div>
        )}

        <p className="subtitle" style={{ marginTop: 4 }}>
          {krMode ? t('home.subtitleKr') : t('home.subtitleUs')}{' '}
          {t('home.matchExtra')}{' '}
          <Link to={simPath}>{t('home.simulatorLink')}</Link>
        </p>

        <div className="generator-actions app-card-surface">
          <div className="generator-actions__row">
            <select
              id="lottoGame"
              value={game}
              onChange={(e) => onGameChange(e.target.value as GameChoice)}
              aria-label={t('home.gameLabel')}
            >
              <option value="us_powerball">{t('home.game_us_powerball')}</option>
              <option value="us_megamillions">{t('home.game_us_megamillions')}</option>
              <option value="kr_lotto645">{t('home.game_kr_lotto645')}</option>
            </select>
          </div>
          <div className="generator-actions__row generator-actions__buttons">
            <button
              type="button"
              className="spin-auto-btn"
              style={{ flex: 1, maxWidth: 420 }}
              onClick={spinFindHistorical}
              disabled={spinning}
              title={t('autoGen.spinBtnTitle')}
              aria-label={t('autoGen.spinBtnAria')}
            >
              <span className="spin-auto-btn__icon" aria-hidden="true">
                <LottoBallsBounceIcon />
              </span>
              <span className="spin-auto-btn__text">{spinning ? t('autoGen.searching') : t('autoGen.searchBtn')}</span>
            </button>
          </div>
          <p className="generator-actions__hint">{t('autoGen.hint')}</p>
        </div>

        {spinning && <AutoSearchHud attemptCount={spinAttemptsLive} onStop={stopSpin} />}

        {spinSummaryAttempts != null && (
          <div className="spin-wheel-summary" aria-live="polite">
            <Trans
              i18nKey={attemptsSummaryKey}
              values={{ count: spinSummaryAttempts }}
              components={{
                countEl: locale === 'ko' ? <strong /> : <span />,
              }}
            />
          </div>
        )}

        <LottoSetList
          sets={autoSets}
          bonusName={bonusName}
          emptyText={t('autoGen.emptySets')}
          getSetLabel={(i) => t('home.setLabel', { n: i + 1 })}
          renderAfterRow={(i) => {
            const m = autoMatches[i];
            return m && isJackpotMatch(rules, m) ? <MatchInfoPanel match={m} rules={rules} bonusName={bonusName} /> : null;
          }}
        />

        {autoSets.length > 0 && (
          <div className="toolbar-wrap">
            <div className="toolbar">
              <div className="toolbar-group">
                <button type="button" className="action-btn" onClick={copyNumbersAuto}>
                  {autoCopyLabel}
                </button>
                <button type="button" className="action-btn" onClick={saveAsImageAuto}>
                  {t('home.save')}
                </button>
              </div>
              <div className="toolbar-divider" />
              <div className="toolbar-group">
                <button
                  type="button"
                  className="share-icon"
                  onClick={() => autoShareText && window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(autoShareText)}`, '_blank')}
                  style={{ background: '#000' }}
                  aria-label={t('home.shareX')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24">
                    <path fill="#fff" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <AdSenseSlot slot="1234567890" label={t('home.adLabel')} />

      <section className="center-block" style={{ marginTop: 28 }}>
        <article className="about-section home-editorial" aria-labelledby="home-editorial-heading">
          <h2 id="home-editorial-heading" style={{ fontSize: '1.15em', marginTop: 8, marginBottom: 12, color: 'var(--text-primary)' }}>
            {t('home.editorialTitle')}
          </h2>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            <Trans i18nKey="home.editorialAffiliation" components={{ strong: <strong /> }} />
          </p>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>{t('home.editorialRetail')}</p>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            <Trans
              i18nKey="home.editorialLinks"
              components={{
                about: <Link to={lp('/about')} />,
                faq: <Link to={lp('/faq')} />,
                story: <Link to={lp('/story')} />,
                privacy: <Link to={lp('/privacy')} />,
              }}
            />
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialHowItWorksTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>{t('home.editorialHowItWorks')}</p>
          <h3 style={{ fontSize: '1.05em', marginTop: 16, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialRandomnessTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>{t('home.editorialRandomness')}</p>
          <h3 style={{ fontSize: '1.05em', marginTop: 16, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialResponsibleTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>{t('home.editorialResponsible')}</p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialOddsTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('home.editorialOddsBody')}
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialDbTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('home.editorialDbBody')}
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8, color: 'var(--text-primary)' }}>
            {t('home.editorialReadMoreTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            <Trans i18nKey="home.editorialReadMoreBody" components={{ articles: <Link to="/articles/" /> }} />
          </p>
        </article>
      </section>
    </main>
  );
}
